import React from "react";
import { styled } from "@mui/material/styles";
import { TableRow, TableCell } from "@mui/material";
import colors from "assets/theme-dark/base/colors";
import { TableSegment } from "../../utils/segmentationTableUtils";
import { cruxyColors } from "../../theme/color-palette";
import SortButton from "../SortButton";

interface SubHeaderRowProps {
  segments: TableSegment[];
  sortColumn: string;
  sortDirection: "asc" | "desc";
  handleSort: (column: string) => void;
  hasOneDataAndOneManualSegment: boolean;
  isManualOnly: boolean;
  isDataOnly: boolean;
  isTwoSameMethod: boolean;
}

const SubHeaderRow: React.FC<SubHeaderRowProps> = ({
  segments,
  sortColumn,
  sortDirection,
  handleSort,
  hasOneDataAndOneManualSegment,
  isManualOnly,
  isDataOnly,
  isTwoSameMethod,
}) => {
  const sortedSegments = isTwoSameMethod ? [...segments] : segments; // No sorting for two manual segments

  return (
    <TableRow>
      {isManualOnly || isDataOnly ? (
        <>
          <SubHeaderTableCell align="center">{isManualOnly ? "Manual" : "Data"}</SubHeaderTableCell>
          <SubHeaderTableCell align="center">
            <SortButton
              column="segmentValue"
              label={`${segments[0].segmentType} (${isManualOnly ? "Manual" : "Data"})`}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              handleSort={handleSort}
              maxLength={30}
            />
          </SubHeaderTableCell>
        </>
      ) : hasOneDataAndOneManualSegment ? (
        <>
          <SubHeaderTableCell align="center">
            <SortButton
              column="range"
              label={`${segments.find((seg) => seg.method === "MANUAL")?.segmentType || "Manual"} (Manual)`}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              handleSort={handleSort}
              maxLength={30}
            />
          </SubHeaderTableCell>
          <SubHeaderTableCell align="center">
            <SortButton
              column="segmentValue"
              label={`${segments.find((seg) => seg.method === "DATA")?.segmentType || "Data"} (Data)`}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              handleSort={handleSort}
              maxLength={30}
            />
          </SubHeaderTableCell>
        </>
      ) : segments.length === 0 ? (
        <SubHeaderTableCell colSpan={2} align="center">
          No Segments Defined
        </SubHeaderTableCell>
      ) : (
        sortedSegments?.map((seg, index) => (
          <SubHeaderTableCell key={`segment-header-${index}`} align="center">
            <SortButton
              column={seg.method === "DATA" ? `segmentValue${index + 1}` : `range${index + 1}`}
              label={`Segment ${index + 1} - ${seg.segmentType} (${seg.method === "DATA" ? "Data" : "Manual"})`}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              handleSort={handleSort}
              maxLength={35}
            />
          </SubHeaderTableCell>
        ))
      )}
      <SubHeaderTableCell align="center">
        <SortButton
          column="total"
          label="# Total"
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          handleSort={handleSort}
        />
      </SubHeaderTableCell>
      <SubHeaderTableCell align="center">
        <SortButton
          column="count1"
          label="# Customers"
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          handleSort={handleSort}
        />
      </SubHeaderTableCell>
      <SubHeaderTableCell align="center">
        <SortButton
          column="count2"
          label="# Prospects"
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          handleSort={handleSort}
        />
      </SubHeaderTableCell>
      {["Min", "Percentile25", "Percentile50", "Percentile75", "Max"].map((metric) => (
        <SubHeaderTableCell key={`driver1-${metric}`} align="center">
          <SortButton
            column={`driver1.${metric.toLowerCase()}`}
            label={
              metric === "Percentile25"
                ? "25%"
                : metric === "Percentile50"
                ? "50%"
                : metric === "Percentile75"
                ? "75%"
                : metric
            }
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            handleSort={handleSort}
          />
        </SubHeaderTableCell>
      ))}
      {["Min", "Percentile25", "Percentile50", "Percentile75", "Max"].map((metric) => (
        <SubHeaderTableCell key={`driver2-${metric}`} align="center">
          <SortButton
            column={`driver2.${metric.toLowerCase()}`}
            label={
              metric === "Percentile25"
                ? "25%"
                : metric === "Percentile50"
                ? "50%"
                : metric === "Percentile75"
                ? "75%"
                : metric
            }
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            handleSort={handleSort}
          />
        </SubHeaderTableCell>
      ))}
    </TableRow>
  );
};

const SubHeaderTableCell = styled(TableCell)(({ theme }) => ({
  border: `1px solid ${colors.grey[300]}`,
  padding: theme.spacing(1.5),
  fontSize: "0.75rem",
  backgroundColor: cruxyColors.green.main,
  color: colors.text.primary,
}));

export default SubHeaderRow;
